/**
 * NTP-style client/server clock-offset estimator (WS-8).
 *
 * Sends `sync:ping` on a fixed cadence, collects `sync:pong` replies into a
 * ring of the last {@link SYNC.PING_SAMPLES} round-trip samples, and keeps the
 * offset derived from the minimum-RTT sample (the one least distorted by
 * queueing/network jitter). `serverNow()` then approximates the server's
 * epoch-millis clock from the client clock plus that offset.
 *
 * The socket is injected (dependency injection) rather than imported as a
 * singleton, so this module is testable and decoupled from WS-5's socket
 * lifecycle. Only the *type* is imported from the socket module.
 *
 * @see @syncreel/shared time.ts estimateClockOffset — the pure offset math.
 * @see Jellyfin SyncPlay — min-RTT sample selection.
 */
import { SYNC, estimateClockOffset, type ClockSample } from '@syncreel/shared';
// WHY: type-only import — we need the typed socket shape but must NOT pull in
// the WS-5 singleton (getSocket/current) here; the socket is passed in.
import type { SyncSocket } from '../socket/socket';

/** Controller surface returned by {@link createClock}. */
export interface ClockController {
  /** Begin the ping interval and listen for pong replies. */
  start(): void;
  /** Stop the interval and remove the pong listener (idempotent). */
  stop(): void;
  /** Best estimate of the server's epoch-millis clock, right now. */
  serverNow(): number;
  /** Drop all samples and fire an immediate ping (used on reconnect). */
  resync(): void;
  /** Current number of collected samples (diagnostic / testing). */
  sampleCount(): number;
}

/**
 * Create a clock-offset estimator bound to the given socket.
 *
 * The caller owns the socket; this controller only adds the ping/pong
 * listeners and a timer. Call {@link ClockController.stop} to release them.
 */
export function createClock(socket: SyncSocket): ClockController {
  let samples: ClockSample[] = [];
  let offsetMs = 0;
  let timer: ReturnType<typeof setInterval> | null = null;
  let started = false;

  const sendPing = (): void => {
    // WHY: t0 is captured as late as possible to minimize measured RTT.
    const t0 = Date.now();
    socket.emit('sync:ping', { t0 });
  };

  const onPong = (payload: { t0: number; t1: number }): void => {
    const t2 = Date.now();
    const sample: ClockSample = { t0: payload.t0, t1: payload.t1, t2 };
    samples.push(sample);
    // Keep only the most recent PING_SAMPLES (ring buffer).
    if (samples.length > SYNC.PING_SAMPLES) {
      samples = samples.slice(samples.length - SYNC.PING_SAMPLES);
    }
    offsetMs = estimateClockOffset(samples);
  };

  const start = (): void => {
    if (started) return;
    started = true;
    socket.on('sync:pong', onPong);
    // WHY: fire one ping immediately so serverNow() is meaningful before the
    // first interval elapses; late joiners need a usable offset right away.
    sendPing();
    timer = setInterval(sendPing, SYNC.PING_INTERVAL_MS);
  };

  const stop = (): void => {
    if (!started) return;
    started = false;
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    socket.off('sync:pong', onPong);
  };

  const resync = (): void => {
    samples = [];
    offsetMs = 0;
    if (started) sendPing();
  };

  const serverNow = (): number => Date.now() + offsetMs;

  const sampleCount = (): number => samples.length;

  return { start, stop, serverNow, resync, sampleCount };
}
